// ============================================================
// OFFER SALARY HELPERS
// ============================================================
// The Annexure-A breakup itself is calculated on the server by
// hr_backend/src/services/salaryStructure.js. These helpers only cover the
// totals that the form shows while the recruiter is typing, so the form can
// never disagree with the letter about which amount is the per-period one.
// ============================================================

/**
 * The flat medical insurance premium. It is part of the fixed side of the
 * offer (section D of Annexure-A) and therefore part of the headline
 * "Total CTC (E + F)". The engine owns the authoritative value.
 */
export const MEDICAL_INSURANCE_ANNUAL = 10000;

/** How many times a variable of each type is paid in a year. */
export const VARIABLE_PERIOD_MULTIPLIERS = {
  monthly: 12,
  quarterly: 4,
  halfYearly: 2,
  yearly: 1,
};

/**
 * Work out how many times a year the selected variable type is paid.
 * An unknown type is treated as a yearly payment so the total is never
 * guessed upwards.
 */
export const variableMultiplier = (variableType) => {
  const type = String(variableType || "").toLowerCase();

  if (type.startsWith("monthly")) return VARIABLE_PERIOD_MULTIPLIERS.monthly;
  if (type.startsWith("quarterly"))
    return VARIABLE_PERIOD_MULTIPLIERS.quarterly;
  if (type.startsWith("half yearly") || type.startsWith("half-yearly"))
    return VARIABLE_PERIOD_MULTIPLIERS.halfYearly;

  return VARIABLE_PERIOD_MULTIPLIERS.yearly;
};

/**
 * Turn a per-period amount into the annual figure.
 *
 * `Number` is used rather than `parseInt` so that a CTC with paise is not
 * silently truncated. The result is rounded the same way the server rounds it
 * (`Math.round`), so the two never disagree by a rupee.
 */
export const annualiseVariable = (variableType, variableAmount) => {
  const amount = Number(variableAmount);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return Math.round(amount * variableMultiplier(variableType));
};

/** Human readable name of each variable frequency. */
export const VARIABLE_PERIOD_LABELS = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  halfYearly: "Half Yearly",
  yearly: "Annual",
};

/** The frequency key used by VARIABLE_PERIOD_MULTIPLIERS for a free-text type. */
export const resolveVariablePeriod = (variableType) => {
  const type = String(variableType || "").trim().toLowerCase();

  if (type.startsWith("monthly")) return "monthly";
  if (type.startsWith("quarterly")) return "quarterly";
  if (type.startsWith("half yearly") || type.startsWith("half-yearly"))
    return "halfYearly";

  return "yearly";
};

/**
 * The totals shown next to the CTC inputs.
 *
 * `offered_ctc_variable_amount` stores the RAW per-period amount and the
 * annualised figure is derived from it, so re-saving an offer can never
 * multiply the bonus a second time. The server recomputes the same numbers
 * on save, so the form and the letter always agree.
 */
export const computeOfferCtcTotals = ({
  fixedCtc,
  variableType,
  variableAmount,
  annualVariablePay,
  annualRetentionBonus,
} = {}) => {
  const fixed = Number(fixedCtc);
  const safeFixed = Number.isFinite(fixed) && fixed > 0 ? fixed : 0;

  const billable = annualiseVariable(variableType, variableAmount);

  const annualPay = Number(annualVariablePay);
  const retention = Number(annualRetentionBonus);

  const variableTotal =
    billable +
    (Number.isFinite(annualPay) && annualPay > 0 ? annualPay : 0) +
    (Number.isFinite(retention) && retention > 0 ? retention : 0);

  return {
    fixed: safeFixed,
    variableTotal,
    // What the `offered_ctc_total` column means: the fixed CTC plus the
    // variable pay, without the flat medical insurance premium.
    fixedPlusVariable: safeFixed + variableTotal,
    // The headline total of the letter (E + F) also carries the flat
    // medical insurance premium.
    total: safeFixed + MEDICAL_INSURANCE_ANNUAL + variableTotal,
  };
};

/** Coerce anything into a non-negative number. */
const toAmount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Read the compensation figures of a saved offer row.
 *
 * The offers list, the offer details modal and the release checklist all
 * print the same numbers, so they all go through this helper. It reads the
 * persisted columns and derives the annualised variable figure the same way
 * the server does, so what the user sees on screen is what the letter says.
 *
 * `offered_ctc_variable_amount` holds the RAW per-period amount of the
 * billable bonus; the annual figure is derived, never stored, which is what
 * stops a re-save from compounding the bonus.
 */
export const getOfferCompensationSummary = (offer = {}) => {
  const fixed = toAmount(offer.offered_ctc_fixed);
  const variableType = offer.offered_ctc_variable || "";
  const perPeriod = toAmount(offer.offered_ctc_variable_amount);
  const annualVariablePay = toAmount(offer.variable_annual_pay_amount);
  const retentionBonus = toAmount(offer.variable_retention_bonus_amount);

  const totals = computeOfferCtcTotals({
    fixedCtc: fixed,
    variableType,
    variableAmount: perPeriod,
    annualVariablePay,
    annualRetentionBonus: retentionBonus,
  });

  const period = resolveVariablePeriod(variableType);

  // The grand total the letter prints as "Total CTC (E+F)". The stored column
  // is authoritative; the computed value is the fallback for rows that were
  // saved before the column existed. An offer with no compensation at all
  // carries no grand total - the medical insurance premium only exists
  // alongside a fixed CTC.
  const storedGrandTotal = toAmount(offer.offered_ctc_gross_total);
  const hasCompensation = fixed > 0 || totals.variableTotal > 0;
  const grandTotal = storedGrandTotal > 0
    ? storedGrandTotal
    : hasCompensation
    ? totals.total
    : 0;

  return {
    fixed,
    // Section E of Annexure-A: the fixed CTC plus the flat medical insurance
    // premium.
    fixedTotal: fixed > 0 ? fixed + MEDICAL_INSURANCE_ANNUAL : 0,
    variableType,
    variablePeriod: period,
    variablePeriodLabel: VARIABLE_PERIOD_LABELS[period],
    // The raw per-period amount the recruiter entered.
    variableAmount: perPeriod,
    // The same amount, annualised.
    variableAmountAnnual: annualiseVariable(variableType, perPeriod),
    annualVariablePay,
    retentionBonus,
    // Section F of Annexure-A.
    variableTotal: totals.variableTotal,
    // The headline total of the offer.
    total: grandTotal,
    // True when any variable pay was explicitly entered for this offer.
    hasVariable: totals.variableTotal > 0,
    // True when the offer carries any compensation at all.
    hasCompensation,
  };
};

/** Format a number the way an offer letter prints money. */
export const formatInr = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `₹${n.toLocaleString("en-IN")}`;
};
